// Maintenance and vendors.
//
// The screen is built around the refusals, not around the lists. A manager opens
// it to send somebody to a property, and the two questions that decide whether
// they may — are this contractor's papers current, and is this spend inside what
// the owner authorised — are answered before the button is offered rather than
// after it is pressed.
//
// So dispatch is never a plain POST. It asks first, shows what is blocking, and
// only then enables the action.

import React, { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import {
    PageHeader, Panel, Tabs, Stat, Pill, Table, Modal, Input, Select, Textarea, Button,
    Notice, Loading, Empty, Field, money, fmtDate, titleCase, statusTone,
} from '../../components/secure/pm/PMKit';
import MaintenanceInboxPanel from '../../components/secure/pm/MaintenanceInboxPanel';

const API = '/api/property-management';

const priorityTone = (p) => ({ emergency: 'bad', urgent: 'warn', routine: 'slate', scheduled: 'indigo' }[p] || 'slate');
const invoiceTone = (s) => ({ received: 'warn', approved: 'indigo', paid: 'good', disputed: 'bad', void: 'slate' }[s] || 'slate');

export default function PMMaintenancePage() {
    const apiClient = useApiClient();
    const [tab, setTab] = useState('work_orders');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [flash, setFlash] = useState(null);
    const [busy, setBusy] = useState(false);

    const [workOrders, setWorkOrders] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [compliance, setCompliance] = useState({ vendors: [] });
    const [invoices, setInvoices] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [properties, setProperties] = useState([]);

    const [detail, setDetail] = useState(null);       // open work order
    const [check, setCheck] = useState(null);         // dispatch check for it
    const [modal, setModal] = useState(null);         // 'wo' | 'vendor' | 'invoice' | 'approval' | 'pay' | 'schedule'
    const [form, setForm] = useState({});

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [wo, v, comp, inv, sch, props] = await Promise.all([
                apiClient.get(`${API}/work-orders`),
                apiClient.get(`${API}/vendors?active=all`),
                apiClient.get(`${API}/vendors/compliance`),
                apiClient.get(`${API}/vendor-invoices`),
                apiClient.get(`${API}/maintenance-schedules`),
                apiClient.get(`${API}/properties`),
            ]);
            setWorkOrders(wo); setVendors(v); setCompliance(comp);
            setInvoices(inv); setSchedules(sch); setProperties(props || []);
        } catch (e) { setError({ message: e.message }); } finally { setLoading(false); }
    }, [apiClient]);

    useEffect(() => { load(); }, [load]);

    const open = async (id) => {
        setError(null);
        try {
            const [d, c] = await Promise.all([
                apiClient.get(`${API}/work-orders/${id}`),
                apiClient.get(`${API}/work-orders/${id}/dispatch-check`),
            ]);
            setDetail(d); setCheck(c);
        } catch (e) { setError({ message: e.message }); }
    };

    const act = async (fn, okMessage) => {
        setBusy(true); setError(null); setFlash(null);
        try {
            const out = await fn();
            setFlash({ tone: 'good', message: okMessage, detail: out && (out.note || out.warning || out.gate_note) });
            setModal(null); setForm({});
            await load();
            if (detail) await open(detail.work_order_id);
        } catch (e) {
            setError({ message: e.message, problems: e.problems || e.payload?.blockers || e.payload?.missing });
        } finally { setBusy(false); }
    };

    if (loading) return <Loading />;

    const openWorkOrders = workOrders.filter(w => !['complete', 'cancelled'].includes(w.status));
    const emergencies = openWorkOrders.filter(w => w.is_emergency);
    const awaitingOwner = openWorkOrders.filter(w => w.owner_approval_required && !w.owner_approved_on);
    const unpaid = invoices.filter(i => ['received', 'approved'].includes(i.status));
    const blocked = compliance.vendors.filter(v => !v.dispatchable);

    // ---------------------------------------------------------------- work orders
    const woColumns = [
        { key: 'title', label: 'Work order',
            render: (w) => (
                <div className="max-w-md">
                    <div className="text-slate-800">
                        {w.is_emergency && <span className="mr-2"><Pill tone="bad">Emergency</Pill></span>}
                        {w.title}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">
                        {w.property_name}{w.unit_number ? ` · unit ${w.unit_number}` : ''}
                        {w.wo_no ? ` · ${w.wo_no}` : ''}
                    </div>
                </div>
            ) },
        { key: 'priority', label: 'Priority', nowrap: true,
            render: (w) => <Pill tone={priorityTone(w.priority)}>{titleCase(w.priority)}</Pill> },
        { key: 'vendor_name', label: 'Vendor',
            render: (w) => w.vendor_name
                ? <div>
                    <div className="text-sm text-slate-700">{w.vendor_name}</div>
                    {w.coi_expires_on && new Date(w.coi_expires_on) < new Date() && (
                        <div className="text-[11px] text-rose-600 font-medium">COI expired {fmtDate(w.coi_expires_on)}</div>
                    )}
                </div>
                : <span className="text-slate-300 text-xs">unassigned</span> },
        { key: 'estimate_amount', label: 'Authorised', nowrap: true,
            render: (w) => (
                <div className="text-right">
                    <div className="tabular-nums text-slate-700">
                        {money(w.nte_amount ?? w.estimate_amount)}
                    </div>
                    {w.owner_approved_amount !== null && w.owner_approved_amount !== undefined && (
                        <div className="text-[11px] text-emerald-700">
                            owner: {money(w.owner_approved_amount)}
                        </div>
                    )}
                    {w.owner_approval_required && !w.owner_approved_on && (
                        <div className="text-[11px] text-amber-700 font-medium">needs owner</div>
                    )}
                </div>
            ) },
        { key: 'status', label: 'Status', nowrap: true,
            render: (w) => <Pill tone={statusTone(w.status)}>{titleCase(w.status)}</Pill> },
    ];

    // ---------------------------------------------------------------- vendors
    const vendorColumns = [
        { key: 'legal_name', label: 'Vendor',
            render: (v) => (
                <div>
                    <div className="text-slate-800">{v.legal_name}</div>
                    <div className="text-xs text-slate-400">{v.dba || v.trades || '—'}</div>
                </div>
            ) },
        { key: 'coi_expires_on', label: 'Insurance', nowrap: true,
            render: (v) => {
                if (!v.coi_expires_on) return <Pill tone="bad">None on file</Pill>;
                const expired = new Date(v.coi_expires_on) < new Date();
                return (
                    <div>
                        <Pill tone={expired ? 'bad' : 'good'}>{expired ? 'Expired' : 'Current'}</Pill>
                        <div className="text-[11px] text-slate-400 mt-0.5">{fmtDate(v.coi_expires_on)}</div>
                    </div>
                );
            } },
        { key: 'w9_on_file', label: 'W-9', nowrap: true,
            render: (v) => v.w9_on_file
                ? <Pill tone="good">On file</Pill>
                : (v.is_corporation
                    ? <span className="text-xs text-slate-400">corporation</span>
                    : <Pill tone="warn">Missing</Pill>) },
        { key: 'is_approved', label: 'Dispatch', nowrap: true,
            render: (v) => v.suspended_on
                ? <Pill tone="bad">Suspended</Pill>
                : (v.is_approved ? <Pill tone="good">Approved</Pill> : <Pill tone="slate">Not approved</Pill>) },
        { key: 'open_work_orders', label: 'Open', nowrap: true,
            render: (v) => <span className="tabular-nums text-slate-600">{v.open_work_orders || 0}</span> },
        { key: 'act', label: '', nowrap: true,
            render: (v) => !v.is_approved && !v.suspended_on
                ? <Button variant="ghost" disabled={busy}
                    onClick={() => act(() => apiClient.post(`${API}/vendors/${v.vendor_id}/approve`, {}),
                        `${v.legal_name} approved for dispatch.`)}>Approve</Button>
                : null },
    ];

    // ---------------------------------------------------------------- invoices
    const invoiceColumns = [
        { key: 'vendor_name', label: 'Invoice',
            render: (i) => (
                <div className="max-w-md">
                    <div className="text-slate-800">{i.vendor_name}</div>
                    <div className="text-xs text-slate-400">
                        {i.invoice_no ? `#${i.invoice_no} · ` : ''}{fmtDate(i.invoice_date)}
                        {i.property_name ? ` · ${i.property_name}` : ''}
                    </div>
                </div>
            ) },
        { key: 'owner_name', label: 'Charged to',
            render: (i) => <span className="text-sm text-slate-600">{i.owner_name}</span> },
        { key: 'amount', label: 'Amount', nowrap: true,
            render: (i) => <div className="text-right tabular-nums text-slate-800">{money(i.amount)}</div> },
        { key: 'status', label: 'Status', nowrap: true,
            render: (i) => (
                <div>
                    <Pill tone={invoiceTone(i.status)}>{titleCase(i.status)}</Pill>
                    {i.over_approval_accepted ? (
                        <div className="text-[11px] text-amber-700 mt-0.5">above authorised</div>
                    ) : null}
                </div>
            ) },
        { key: 'act', label: '', nowrap: true,
            render: (i) => {
                if (i.status === 'received') {
                    return <Button variant="ghost" disabled={busy}
                        onClick={() => act(() => apiClient.post(`${API}/vendor-invoices/${i.invoice_id}/approve`, {}),
                            'Invoice approved and charged to the owner.')}>Approve</Button>;
                }
                if (i.status === 'approved') {
                    return <Button variant="ghost" disabled={busy}
                        onClick={() => { setModal('pay'); setForm({ invoice_id: i.invoice_id, method: 'check' }); }}>
                        Pay</Button>;
                }
                return null;
            } },
    ];

    const scheduleColumns = [
        { key: 'label', label: 'Recurring work',
            render: (s) => (
                <div className="max-w-md">
                    <div className="text-slate-800">{s.label}</div>
                    <div className="text-xs text-slate-400">
                        {s.property_name}{s.unit_number ? ` · unit ${s.unit_number}` : ''} · {titleCase(s.frequency)}
                    </div>
                    {s.authority && <div className="text-[11px] text-indigo-700/70 mt-0.5 italic">{s.authority}</div>}
                </div>
            ) },
        { key: 'vendor_name', label: 'Usual vendor',
            render: (s) => s.vendor_name || <span className="text-slate-300 text-xs">—</span> },
        { key: 'next_due_on', label: 'Next due', nowrap: true,
            render: (s) => (
                <div>
                    <div className="text-slate-700">{fmtDate(s.next_due_on)}</div>
                    <div className={`text-xs ${s.days_out < 0 ? 'text-rose-600 font-semibold' : 'text-slate-400'}`}>
                        {s.days_out < 0 ? `${Math.abs(s.days_out)} days overdue`
                            : s.days_out === 0 ? 'today' : `in ${s.days_out} days`}
                    </div>
                </div>
            ) },
    ];

    return (
        <div>
            <PageHeader
                title="Maintenance & vendors"
                subtitle="Who may be sent, what they may spend, and what the owner was charged."
            >
                <Button variant="ghost" onClick={() => { setModal('vendor'); setForm({ issue_1099: 1 }); }}>
                    Add vendor
                </Button>
                <Button onClick={() => { setModal('wo'); setForm({ priority: 'routine', reported_by: 'tenant' }); }}>
                    New work order
                </Button>
            </PageHeader>

            {error && <Notice tone="bad" title="That was refused" message={error.message}
                problems={error.problems} onDismiss={() => setError(null)} />}
            {flash && <Notice tone={flash.tone} message={flash.message}
                problems={flash.detail ? [flash.detail] : null} onDismiss={() => setFlash(null)} />}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                <Stat label="Open work orders" value={openWorkOrders.length}
                    sub={emergencies.length ? `${emergencies.length} emergency` : 'none urgent'}
                    tone={emergencies.length ? 'bad' : 'default'} />
                <Stat label="Awaiting the owner" value={awaitingOwner.length}
                    sub="above the authorised limit" tone={awaitingOwner.length ? 'warn' : 'default'} />
                <Stat label="Unpaid invoices" value={unpaid.length}
                    sub={money(unpaid.reduce((s, i) => s + Number(i.amount), 0))} />
                <Stat label="Vendors blocked" value={blocked.length}
                    sub="papers lapsed or missing" tone={blocked.length ? 'bad' : 'good'} />
            </div>

            <Tabs
                active={tab} onChange={setTab}
                tabs={[
                    { key: 'work_orders', label: 'Work orders', count: openWorkOrders.length },
                    { key: 'vendors', label: 'Vendors', count: vendors.length },
                    { key: 'invoices', label: 'Invoices', count: unpaid.length },
                    { key: 'preventive', label: 'Preventive', count: schedules.length },
                    { key: 'inbox', label: 'Text inbox' },
                ]}
            />

            {tab === 'inbox' && <MaintenanceInboxPanel apiClient={apiClient} properties={properties} onConverted={load} />}

            {tab === 'work_orders' && (
                <Panel title="Open work orders"
                    subtitle="Sorted by what is on fire. Click one to see who authorised what.">
                    <Table columns={woColumns} rows={openWorkOrders}
                        onRowClick={(w) => open(w.work_order_id)}
                        empty="No open work orders." />
                </Panel>
            )}

            {tab === 'vendors' && (
                <>
                    {blocked.length > 0 && (
                        <Notice tone="warn" title={`${blocked.length} vendor(s) cannot be dispatched`}
                            message={compliance.note}
                            problems={blocked.map(v => `${v.legal_name} — ${
                                !v.coi_expires_on ? 'no certificate of insurance on file'
                                    : v.coi_days < 0 ? `certificate expired ${fmtDate(v.coi_expires_on)}`
                                        : v.suspended_on ? 'suspended' : 'not approved'}`)} />
                    )}
                    <Panel title="Vendor register"
                        subtitle="Approval is checked at dispatch, not at creation — a certificate that was current in January is not current now.">
                        <Table columns={vendorColumns} rows={vendors} empty="No vendors recorded." />
                    </Panel>
                </>
            )}

            {tab === 'invoices' && (
                <Panel title="Vendor invoices"
                    subtitle="Approving charges the owner. Paying moves trust money, and that is the managing broker’s act."
                    actions={<Button variant="ghost"
                        onClick={() => { setModal('invoice'); setForm({ invoice_date: new Date().toISOString().slice(0, 10) }); }}>
                        Record an invoice</Button>}>
                    <Table columns={invoiceColumns} rows={invoices} empty="No invoices recorded." />
                </Panel>
            )}

            {tab === 'preventive' && (
                <Panel title="Preventive maintenance"
                    subtitle="The boiler service and the backflow test. Raised ahead of the due date, because a work order raised on the day is already late."
                    actions={
                        <div className="flex gap-2">
                            <Button variant="ghost"
                                onClick={() => { setModal('schedule'); setForm({ frequency: 'annual', lead_days: 14 }); }}>
                                Add schedule</Button>
                            <Button disabled={busy}
                                onClick={() => act(() => apiClient.post(`${API}/maintenance-schedules/generate`, {}),
                                    'Raised the work orders coming due.')}>
                                Raise what is due</Button>
                        </div>}>
                    <Table columns={scheduleColumns} rows={schedules}
                        empty="No recurring maintenance is scheduled." />
                </Panel>
            )}

            {/* ------------------------------------------------ work order detail */}
            {detail && (
                <Modal wide title={detail.title}
                    subtitle={`${detail.property_name}${detail.unit_number ? ` · unit ${detail.unit_number}` : ''}`}
                    onClose={() => { setDetail(null); setCheck(null); }}>

                    {check && !check.ok && (
                        <Notice tone="bad" title="This cannot be dispatched yet" problems={check.blockers} />
                    )}
                    {check && check.warnings.length > 0 && (
                        <Notice tone="warn" title="Worth knowing first" problems={check.warnings} />
                    )}

                    <div className="grid sm:grid-cols-2 gap-x-8">
                        <dl>
                            <Field label="Status" value={titleCase(detail.status)} />
                            <Field label="Vendor" value={detail.vendor_name} />
                            <Field label="Estimate" value={money(detail.estimate_amount)} />
                            <Field label="Not to exceed" value={money(detail.nte_amount)} />
                        </dl>
                        <dl>
                            <Field label="Owner approved"
                                value={detail.owner_approved_amount !== null && detail.owner_approved_amount !== undefined
                                    ? `${money(detail.owner_approved_amount)} on ${fmtDate(detail.owner_approved_on)}`
                                    : null}
                                hint={detail.owner_approval_channel ? `via ${detail.owner_approval_channel}` : null} />
                            <Field label="Emergency"
                                value={detail.is_emergency ? detail.emergency_reason : null}
                                hint={detail.is_emergency
                                    ? 'Raises the owner’s ceiling and releases money ahead of the monthly sweep.'
                                    : null} />
                            <Field label="Entry notice given" value={fmtDate(detail.entry_notice_given_on)}
                                hint={check?.entry_notice?.authority} />
                            <Field label="Invoiced"
                                value={money(detail.invoices.reduce((s, i) => s + Number(i.amount), 0))} />
                        </dl>
                    </div>

                    <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-slate-100">
                        {!['complete', 'cancelled'].includes(detail.status) && (
                            <>
                                <Button disabled={busy || !(check && check.ok)}
                                    onClick={() => act(
                                        () => apiClient.post(`${API}/work-orders/${detail.work_order_id}/dispatch`, {}),
                                        'Dispatched.')}>
                                    Dispatch
                                </Button>
                                <Button variant="ghost"
                                    onClick={() => { setModal('approval'); setForm({ channel: 'email' }); }}>
                                    Record owner approval
                                </Button>
                                <Button variant="ghost" disabled={busy}
                                    onClick={() => act(
                                        () => apiClient.post(`${API}/work-orders/${detail.work_order_id}/entry-notice`, {}),
                                        'Entry notice recorded as given today.')}>
                                    Notice given today
                                </Button>
                                <Button variant="subtle" disabled={busy}
                                    onClick={() => act(
                                        () => apiClient.post(`${API}/work-orders/${detail.work_order_id}/complete`, {}),
                                        'Marked complete.')}>
                                    Complete
                                </Button>
                            </>
                        )}
                    </div>

                    <div className="mt-6">
                        <h4 className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">
                            Authority trail
                        </h4>
                        <ul className="space-y-2">
                            {detail.events.map(e => (
                                <li key={e.event_id} className="text-sm border-l-2 border-slate-200 pl-3">
                                    <div className="text-slate-700">
                                        {titleCase(e.event)}
                                        {e.amount !== null && <span className="tabular-nums"> — {money(e.amount)}</span>}
                                    </div>
                                    {e.authority && <div className="text-xs text-indigo-700/80">{e.authority}</div>}
                                    {e.detail && <div className="text-xs text-slate-500">{e.detail}</div>}
                                    <div className="text-[11px] text-slate-400">
                                        {fmtDate(e.created_at)}{e.acted_by ? ` · ${e.acted_by}` : ''}
                                    </div>
                                </li>
                            ))}
                            {detail.events.length === 0 && (
                                <li className="text-sm text-slate-400">Nothing recorded yet.</li>
                            )}
                        </ul>
                    </div>
                </Modal>
            )}

            {/* ------------------------------------------------ new work order */}
            {modal === 'wo' && (
                <Modal title="New work order"
                    subtitle="If the figure is above what the owner authorised, this will say so now rather than at dispatch."
                    onClose={() => setModal(null)}>
                    <Select label="Property" value={form.pm_property_id || ''}
                        onChange={(e) => setForm({ ...form, pm_property_id: e.target.value })}
                        options={[{ value: '', label: 'Select…' },
                            ...properties.map(p => ({ value: p.pm_property_id, label: p.name }))]} />
                    <Input label="Title" value={form.title || ''}
                        onChange={(e) => setForm({ ...form, title: e.target.value })} />
                    <Textarea label="Description" value={form.description || ''}
                        onChange={(e) => setForm({ ...form, description: e.target.value })} />
                    <div className="grid grid-cols-2 gap-3">
                        <Select label="Priority" value={form.priority}
                            onChange={(e) => setForm({ ...form, priority: e.target.value })}
                            options={['emergency', 'urgent', 'routine', 'scheduled']} />
                        <Select label="Vendor" value={form.vendor_id || ''}
                            onChange={(e) => setForm({ ...form, vendor_id: e.target.value })}
                            options={[{ value: '', label: 'Assign later' },
                                ...vendors.filter(v => v.is_approved && !v.suspended_on)
                                    .map(v => ({ value: v.vendor_id, label: v.legal_name }))]} />
                        <Input label="Estimate" type="number" step="0.01" value={form.estimate_amount || ''}
                            onChange={(e) => setForm({ ...form, estimate_amount: e.target.value })} />
                        <Input label="Not to exceed" type="number" step="0.01" value={form.nte_amount || ''}
                            hint="The ceiling checked against the owner’s limit."
                            onChange={(e) => setForm({ ...form, nte_amount: e.target.value })} />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-slate-700 mb-2">
                        <input type="checkbox" checked={!!form.is_emergency}
                            onChange={(e) => setForm({ ...form, is_emergency: e.target.checked })} />
                        This is an emergency
                    </label>
                    {form.is_emergency && (
                        <Input label="Why it is an emergency" value={form.emergency_reason || ''}
                            hint="Recorded on the file. The emergency provision raises the owner’s ceiling and releases money ahead of the monthly sweep — it does not remove the ceiling."
                            onChange={(e) => setForm({ ...form, emergency_reason: e.target.value })} />
                    )}
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
                        <Button disabled={busy}
                            onClick={() => act(() => apiClient.post(`${API}/work-orders`, form),
                                'Work order raised.')}>Raise it</Button>
                    </div>
                </Modal>
            )}

            {/* ------------------------------------------------ owner approval */}
            {modal === 'approval' && detail && (
                <Modal title="Record the owner’s approval"
                    subtitle="A figure and a channel, not a yes. The invoice that arrives later is checked against this."
                    onClose={() => setModal(null)}>
                    <Input label="Amount approved" type="number" step="0.01" value={form.amount || ''}
                        onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                    <Select label="How it was given" value={form.channel}
                        onChange={(e) => setForm({ ...form, channel: e.target.value })}
                        options={['portal', 'email', 'phone', 'in_person', 'written']} />
                    <Input label="Date approved" type="date" value={form.approved_on || ''}
                        onChange={(e) => setForm({ ...form, approved_on: e.target.value })} />
                    <Textarea label="Note" value={form.note || ''}
                        onChange={(e) => setForm({ ...form, note: e.target.value })} />
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
                        <Button disabled={busy}
                            onClick={() => act(
                                () => apiClient.post(`${API}/work-orders/${detail.work_order_id}/owner-approval`, form),
                                'Owner approval recorded.')}>Record it</Button>
                    </div>
                </Modal>
            )}

            {/* ------------------------------------------------ vendor */}
            {modal === 'vendor' && (
                <Modal title="Add a vendor"
                    subtitle="Recorded now, approved once the certificate and W-9 are on file."
                    onClose={() => setModal(null)}>
                    <Input label="Legal name" value={form.legal_name || ''}
                        onChange={(e) => setForm({ ...form, legal_name: e.target.value })} />
                    <div className="grid grid-cols-2 gap-3">
                        <Input label="Trades" value={form.trades || ''} placeholder="plumbing, HVAC"
                            onChange={(e) => setForm({ ...form, trades: e.target.value })} />
                        <Input label="Phone" value={form.phone || ''}
                            onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                        <Input label="Insurance carrier" value={form.insurance_carrier || ''}
                            onChange={(e) => setForm({ ...form, insurance_carrier: e.target.value })} />
                        <Input label="Certificate expires" type="date" value={form.coi_expires_on || ''}
                            onChange={(e) => setForm({ ...form, coi_expires_on: e.target.value })} />
                        <Input label="Licence number" value={form.license_number || ''}
                            onChange={(e) => setForm({ ...form, license_number: e.target.value })} />
                        <Input label="Licence expires" type="date" value={form.license_expires_on || ''}
                            onChange={(e) => setForm({ ...form, license_expires_on: e.target.value })} />
                        <Input label="TIN" value={form.tin || ''}
                            onChange={(e) => setForm({ ...form, tin: e.target.value })} />
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-slate-700 mb-2">
                        <label className="flex items-center gap-2">
                            <input type="checkbox" checked={!!form.w9_on_file}
                                onChange={(e) => setForm({ ...form, w9_on_file: e.target.checked ? 1 : 0 })} />
                            W-9 on file
                        </label>
                        <label className="flex items-center gap-2">
                            <input type="checkbox" checked={!!form.is_corporation}
                                onChange={(e) => setForm({ ...form, is_corporation: e.target.checked ? 1 : 0 })} />
                            Corporation
                        </label>
                    </div>
                    <p className="text-xs text-slate-400 mb-3">
                        Payments to a corporation are generally outside 1099-NEC reporting. This reads the box
                        rather than guessing from “Inc” in the name.
                    </p>
                    <div className="flex justify-end gap-2 mt-2">
                        <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
                        <Button disabled={busy}
                            onClick={() => act(() => apiClient.post(`${API}/vendors`, form),
                                'Vendor recorded.')}>Add</Button>
                    </div>
                </Modal>
            )}

            {/* ------------------------------------------------ invoice */}
            {modal === 'invoice' && (
                <Modal title="Record a vendor invoice"
                    subtitle="This charges nobody yet. Approving it charges the owner; paying it moves trust money."
                    onClose={() => setModal(null)}>
                    <Select label="Vendor" value={form.vendor_id || ''}
                        onChange={(e) => setForm({ ...form, vendor_id: e.target.value })}
                        options={[{ value: '', label: 'Select…' },
                            ...vendors.map(v => ({ value: v.vendor_id, label: v.legal_name }))]} />
                    <Select label="Property" value={form.pm_property_id || ''}
                        onChange={(e) => setForm({ ...form, pm_property_id: e.target.value })}
                        options={[{ value: '', label: 'Select…' },
                            ...properties.map(p => ({ value: p.pm_property_id, label: p.name }))]} />
                    <div className="grid grid-cols-2 gap-3">
                        <Input label="Invoice number" value={form.invoice_no || ''}
                            onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} />
                        <Input label="Invoice date" type="date" value={form.invoice_date || ''}
                            onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} />
                        <Input label="Amount" type="number" step="0.01" value={form.amount || ''}
                            onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                        <Select label="Work order" value={form.work_order_id || ''}
                            onChange={(e) => setForm({ ...form, work_order_id: e.target.value })}
                            options={[{ value: '', label: 'None' },
                                ...openWorkOrders.map(w => ({ value: w.work_order_id, label: w.title }))]} />
                    </div>
                    <Input label="Description" value={form.description || ''}
                        onChange={(e) => setForm({ ...form, description: e.target.value })} />
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
                        <Button disabled={busy}
                            onClick={() => act(() => apiClient.post(`${API}/vendor-invoices`, form),
                                'Invoice recorded.')}>Record</Button>
                    </div>
                </Modal>
            )}

            {/* ------------------------------------------------ pay */}
            {modal === 'pay' && (
                <Modal title="Pay this invoice from trust"
                    subtitle="Refused if the month’s fee cycle has not been swept, or if it would spend another client’s money."
                    onClose={() => setModal(null)}>
                    <Select label="Method" value={form.method}
                        onChange={(e) => setForm({ ...form, method: e.target.value })}
                        options={['check', 'ach', 'wire', 'card', 'other']} />
                    <Input label="Reference" value={form.reference || ''} hint="Cheque number."
                        onChange={(e) => setForm({ ...form, reference: e.target.value })} />
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
                        <Button disabled={busy}
                            onClick={() => act(
                                () => apiClient.post(`${API}/vendor-invoices/${form.invoice_id}/pay`, form),
                                'Paid from the trust account.')}>Pay</Button>
                    </div>
                </Modal>
            )}

            {/* ------------------------------------------------ schedule */}
            {modal === 'schedule' && (
                <Modal title="Recurring maintenance" onClose={() => setModal(null)}>
                    <Select label="Property" value={form.pm_property_id || ''}
                        onChange={(e) => setForm({ ...form, pm_property_id: e.target.value })}
                        options={[{ value: '', label: 'Select…' },
                            ...properties.map(p => ({ value: p.pm_property_id, label: p.name }))]} />
                    <Input label="What recurs" value={form.label || ''} placeholder="Boiler service"
                        onChange={(e) => setForm({ ...form, label: e.target.value })} />
                    <div className="grid grid-cols-2 gap-3">
                        <Select label="Frequency" value={form.frequency}
                            onChange={(e) => setForm({ ...form, frequency: e.target.value })}
                            options={['monthly', 'quarterly', 'semiannual', 'annual', 'seasonal']} />
                        <Input label="Raise this many days early" type="number" value={form.lead_days}
                            onChange={(e) => setForm({ ...form, lead_days: e.target.value })} />
                        <Input label="First due" type="date" value={form.next_due_on || ''}
                            onChange={(e) => setForm({ ...form, next_due_on: e.target.value })} />
                        <Select label="Usual vendor" value={form.default_vendor_id || ''}
                            onChange={(e) => setForm({ ...form, default_vendor_id: e.target.value })}
                            options={[{ value: '', label: 'None' },
                                ...vendors.map(v => ({ value: v.vendor_id, label: v.legal_name }))]} />
                    </div>
                    <Input label="Required by" value={form.authority || ''}
                        hint="A municipal code, an insurance condition, a warranty term — where the requirement comes from, when it comes from somewhere."
                        onChange={(e) => setForm({ ...form, authority: e.target.value })} />
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
                        <Button disabled={busy}
                            onClick={() => act(() => apiClient.post(`${API}/maintenance-schedules`, form),
                                'Schedule added.')}>Add</Button>
                    </div>
                </Modal>
            )}

            {workOrders.length === 0 && vendors.length === 0 && (
                <Empty title="Nothing to maintain yet"
                    message="Add the contractors you use, then raise work orders against them." />
            )}
        </div>
    );
}
